/**
 * What a finished restore does next — and how long it says so first.
 *
 * WHY THIS IS A MODULE AND NOT TWO LINES IN THE SCREEN
 * ----------------------------------------------------
 * The screen used to set the success phase and assign `window.location.hash` in
 * the same tick:
 *
 *   setPhase({ kind: 'done', message: `Restored ${n} business(es).` });
 *   window.location.hash = '#/settings/business';
 *
 * The hash change re-routes, and re-routing unmounts the backup screen, so React
 * never painted the confirmation. The message was invisible twice over: a user
 * never read what had just happened to their data, and the E2E specs that wait
 * for `backup-result` timed out on a restore that had actually SUCCEEDED — the
 * page snapshot at failure was the Business Profile, not an error panel.
 *
 * Removing the navigation would "fix" that by deleting the workflow, so the
 * navigation stays and the confirmation is given time to be read. How much time
 * is policy, so it lives here where it can be tested without mounting React.
 */

/** Long enough to read one sentence; short enough not to feel stuck. */
export const RESTORE_RESULT_DWELL_MS = 2500;

/** A completed restore continues to the business list, where the data now is. */
export const RESTORE_COMPLETED_ROUTE = '#/settings/business';

export interface RestoreCompletion {
  /** The line the result panel shows, e.g. `Restored 2 business(es).` */
  readonly message: string;
  /** Where to go once that line has been read. */
  readonly navigateTo: string;
  /** How long the line stays on screen first. Never zero. */
  readonly afterMs: number;
}

/**
 * The confirmation to show after a restore, and when to move on from it.
 *
 * `afterMs` is deliberately non-zero: navigating in the same tick is the bug
 * above, so a caller asking for zero is asking for an unreadable success
 * message. It is also bounded — parking the user on this screen is a different
 * bug, not a fix.
 */
export function planRestoreCompletion(businesses: number): RestoreCompletion {
  return {
    message: `Restored ${businesses} business(es).`,
    navigateTo: RESTORE_COMPLETED_ROUTE,
    afterMs: RESTORE_RESULT_DWELL_MS,
  };
}
